import React, { useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LANG_NAMES, STUDY_LANGUAGE_CODES } from "@/types/linguadeck";

interface LanguageSelectorProps {
  value: string;
  onChange: (code: string | undefined) => void;
  placeholder: string;
  compact?: boolean;
}

export default function LanguageSelector({
  value,
  onChange,
  placeholder,
  compact = false,
}: LanguageSelectorProps) {
  const [open, setOpen] = useState(false);
  const displayText = value ? LANG_NAMES[value as keyof typeof LANG_NAMES] ?? value : placeholder;
  const handleSelect = (code: string | undefined) => {
    onChange(code);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400 transition-colors cursor-pointer focus:outline-none focus:ring-0 ${compact ? "text-[11px] mt-1" : "text-sm mt-1"}`}
        >
          <span>{displayText}</span>
          <ChevronDown size={compact ? 12 : 14} className="shrink-0 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={4}
        className="z-[120] w-auto min-w-[180px] p-0 rounded-xl border border-gray-100 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] shadow-lg"
      >
        <div className="py-1 max-h-[min(280px,50vh)] overflow-y-auto overscroll-contain">
          <button
            type="button"
            onClick={() => handleSelect(undefined)}
            className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-500/10 hover:text-purple-700 dark:hover:text-purple-400 transition-colors ${!value ? "font-medium text-purple-600 dark:text-purple-400" : ""}`}
          >
            <span>{placeholder}</span>
            {!value && <Check size={16} className="shrink-0" strokeWidth={2.5} />}
          </button>
          {STUDY_LANGUAGE_CODES.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => handleSelect(code)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-500/10 hover:text-purple-700 dark:hover:text-purple-400 transition-colors ${value === code ? "font-medium text-purple-600 dark:text-purple-400" : ""}`}
            >
              <span>{LANG_NAMES[code]}</span>
              {value === code && <Check size={16} className="shrink-0" strokeWidth={2.5} />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
